import { X } from '@tamagui/lucide-icons-2'
import { EditModeProvider } from 'purchasers/editMode'
import { Dialog, ScrollView, SizableText, View, XStack, YStack } from 'tamagui'

interface DrawerProps {
    open: boolean
    onClose: () => void
    title: string
    subtitle?: React.ReactNode
    /** Panel width on wide screens (caps at viewport width). */
    width?: number
    /** Sticky footer content (e.g. Save / Cancel / Delete). */
    footer?: React.ReactNode
    children: React.ReactNode
}

/**
 * Right-anchored slide-over panel (BuyerFlo design system). Built on Tamagui's
 * Dialog so it inherits the focus trap, escape handling, and body-scroll lock;
 * the content is repositioned to a full-height sheet on the right edge.
 */
export function Drawer({ open, onClose, title, subtitle, width = 520, footer, children }: DrawerProps) {
    return (
        <EditModeProvider defaultEdit={true}>
            <Dialog
                modal
                open={open}
                onOpenChange={next => {
                    if (!next) onClose()
                }}
            >
                <Dialog.Portal>
                    <Dialog.Overlay
                        key="overlay"
                        animation="quick"
                        opacity={0.4}
                        backgroundColor="$overlay"
                        enterStyle={{ opacity: 0 }}
                        exitStyle={{ opacity: 0 }}
                    />
                    <Dialog.Content
                        key="content"
                        animation="quick"
                        enterStyle={{ x: 40, opacity: 0 }}
                        exitStyle={{ x: 40, opacity: 0 }}
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        height="100vh"
                        maxHeight="100vh"
                        width={width}
                        maxWidth="100vw"
                        borderRadius={0}
                        borderLeftWidth={1}
                        borderColor="$borderColor"
                        padding={0}
                        overflow="hidden"
                        display="flex"
                        flexDirection="column"
                        backgroundColor="$background"
                    >
                        <XStack
                            paddingHorizontal="$5"
                            paddingVertical="$4"
                            gap="$3"
                            alignItems="flex-start"
                            borderBottomWidth={1}
                            borderBottomColor="$borderColor"
                        >
                            <YStack
                                flex={1}
                                minWidth={0}
                                gap="$1"
                            >
                                <SizableText
                                    size="$6"
                                    fontWeight="700"
                                    letterSpacing={-0.3}
                                >
                                    {title}
                                </SizableText>
                                {subtitle ? (
                                    <SizableText
                                        size="$1"
                                        color="$colorMuted"
                                    >
                                        {subtitle}
                                    </SizableText>
                                ) : null}
                            </YStack>
                            <View
                                width={32}
                                height={32}
                                alignItems="center"
                                justifyContent="center"
                                borderRadius="$3"
                                cursor="pointer"
                                animation="quick"
                                hoverStyle={{ backgroundColor: '$backgroundHover' }}
                                onPress={onClose}
                            >
                                <X
                                    size={16}
                                    color="$colorMuted"
                                />
                            </View>
                        </XStack>

                        <ScrollView flex={1}>
                            <YStack padding="$5">{children}</YStack>
                        </ScrollView>

                        {footer ? (
                            <XStack
                                paddingHorizontal="$5"
                                paddingVertical="$4"
                                gap="$3"
                                alignItems="center"
                                justifyContent="space-between"
                                borderTopWidth={1}
                                borderTopColor="$borderColor"
                            >
                                {footer}
                            </XStack>
                        ) : null}
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog>
        </EditModeProvider>
    )
}
